import { useDispatch, useSelector } from 'react-redux';
import { CONTENT_SAVE_REQUEST_FAILED } from '../../../redux/actions/actionTypes';
import { contentPublishFailed, updateOptimisticLockingForRequest, contentPublishSucceeded, requestedContentPublish
// @ts-expect-error Untyped module
} from '../../../redux/actions/appActions';
// @ts-expect-error Untyped module
import { logSentryWithRecentReduxHistory } from '../../../redux/actions/errorActions';
import { EditorMetricsTracker } from '../../../utils/metricsTracker';
import { getIsFormValid } from '../../../redux/selectors/formStatusSelectors';
import { PublishActionTypes } from '../types/publishButtonTypes';
import { useCallback, useMemo } from 'react';
import http from 'hub-http/clients/apiClient';
import { contentIsPublished } from 'content-management-data-lib';
import { PublishTypes } from '../../../constants/PublishTypes';
import useContentCrmObject from './useContentCrmObject';
const IGNORE_ERROR_CODE = [401, 403];
const cmpSchedulePublishUnpublishAPICallback = ({
  contentObjectType,
  objectId,
  schedulePublishDate,
  scheduleUnpublishDate,
  unpublishExpiryUrl
}) => {
  return http.postWithResponse(`content-management/publication/v1/${contentObjectType}/${objectId}/schedule/publish-and-unpublish/async`, {
    data: {
      schedulePublishDate,
      scheduleUnpublishDate,
      unpublishExpiryUrl
    }
  });
};
const cmpPublishUnpublishAPICallback = ({
  isUnpublish,
  contentObjectType,
  objectId,
  overridePublishDate
}) => {
  return http.postWithResponse(`content-management/publication/v1/${contentObjectType}/${objectId}/${isUnpublish ? 'unpublish' : 'publish'}`, {
    data: {
      overridePublishDate
    }
  });
};
const getScheduleType = (isPublished, publishDate, unpublishDate) => {
  if (publishDate) {
    if (isPublished) {
      return 'scheduledUpdate';
    }
    return 'scheduledPublish';
  } else if (unpublishDate) {
    return 'scheduledUnpublish';
  }
  return undefined;
};
export const useCMPPublishActionHandlers = ({
  endTourIfAny,
  saveAction,
  onPublishSuccess,
  onPublishError,
  trackPublishEvent,
  onUnpublishSuccess,
  onUnpublishError
}) => {
  const {
    contentObjectType,
    reconcileContentCrmObjectForPublishActions,
    contentCrmObjectId,
    contentCrmObject
  } = useContentCrmObject();
  const dispatch = useDispatch();
  const isFormValid = useSelector(getIsFormValid);
  const dispatchUpdateOptimisticLocking = useCallback((xhr, hasSaved) => {
    dispatch(updateOptimisticLockingForRequest({
      hasSaved,
      xLastModifiedTimestampHeader: xhr.getResponseHeader('x-last-modified-timestamp')
    }));
  }, [dispatch]);
  const actionHandler = useCallback(async ({
    isUnpublish = false,
    isScheduling = false,
    schedulePublishDate,
    scheduleUnpublishDate,
    publishType,
    unpublishExpiryUrl,
    overridePublishDate
  } = {}) => {
    if (endTourIfAny) {
      endTourIfAny();
    }
    if (!isFormValid) {
      return;
    }
    dispatch(requestedContentPublish());
    const trackPublishType = isUnpublish ? 'unpublish' : publishType;
    const scheduleType = isScheduling ? getScheduleType(contentIsPublished(contentCrmObject), schedulePublishDate, scheduleUnpublishDate) : undefined;
    try {
      const {
        type
      } = await dispatch(saveAction({
        isSaveBeforePublish: true
      }));
      if (type === CONTENT_SAVE_REQUEST_FAILED) {
        dispatch(contentPublishFailed());
        // return early if save failed to prevent publishing with unsaved changes
        return;
      }
      if (isScheduling) {
        const {
          data,
          xhr
        } = await cmpSchedulePublishUnpublishAPICallback({
          contentObjectType,
          objectId: contentCrmObjectId,
          schedulePublishDate,
          scheduleUnpublishDate,
          unpublishExpiryUrl
        });
        dispatchUpdateOptimisticLocking(xhr, true);
        if (data.schedulePublishLocator) {
          reconcileContentCrmObjectForPublishActions(PublishActionTypes.SCHEDULE_PUBLISH, {
            schedulePublishDate
          });
        }
        if (data.scheduleUnpublishLocator) {
          reconcileContentCrmObjectForPublishActions(PublishActionTypes.SCHEDULE_UNPUBLISH, {
            scheduleUnpublishDate,
            unpublishExpiryUrl
          });
        }
      } else {
        const {
          xhr
        } = await cmpPublishUnpublishAPICallback({
          isUnpublish,
          contentObjectType,
          objectId: contentCrmObjectId,
          overridePublishDate
        });
        dispatchUpdateOptimisticLocking(xhr, true);
        reconcileContentCrmObjectForPublishActions(isUnpublish ? PublishActionTypes.UNPUBLISH : PublishActionTypes.PUBLISH);
      }
      if (isUnpublish) {
        onUnpublishSuccess();
      } else {
        onPublishSuccess(publishType, {
          schedulePublishDate,
          publishDate: overridePublishDate
        });
      }
      trackPublishEvent(true, trackPublishType, scheduleType);
      dispatch(contentPublishSucceeded());
    } catch (errorResponse) {
      if (!IGNORE_ERROR_CODE.includes(errorResponse.status)) {
        EditorMetricsTracker.counter('publish-errors').increment();
        dispatch(logSentryWithRecentReduxHistory('Publish errors', {
          errorResponse: JSON.stringify(errorResponse.responseJSON),
          errorStatus: errorResponse.status
        }, 'publishError'));
      }
      if (isUnpublish) {
        onUnpublishError(errorResponse);
      } else {
        onPublishError(errorResponse);
      }
      trackPublishEvent(false, trackPublishType, scheduleType);
      if (errorResponse && errorResponse.xhr) {
        dispatchUpdateOptimisticLocking(errorResponse.xhr, false);
      }
      dispatch(contentPublishFailed());
    }
  }, [endTourIfAny, isFormValid, dispatch, saveAction, contentCrmObject, contentObjectType, trackPublishEvent, contentCrmObjectId, dispatchUpdateOptimisticLocking, reconcileContentCrmObjectForPublishActions, onUnpublishSuccess, onPublishSuccess, onUnpublishError, onPublishError]);
  return useMemo(() => ({
    publish: (publishType, overridePublishDate) => {
      actionHandler({
        publishType,
        overridePublishDate
      }).catch(errorResponse => onPublishError(errorResponse));
    },
    unpublish: () => {
      actionHandler({
        isUnpublish: true
      }).catch(errorResponse => onUnpublishError(errorResponse));
    },
    schedulePublishUnpublish: ({
      schedulePublishDate,
      scheduleUnpublishDate,
      unpublishExpiryUrl
    }) => {
      actionHandler({
        schedulePublishDate,
        scheduleUnpublishDate,
        unpublishExpiryUrl,
        isScheduling: true,
        publishType: PublishTypes.schedule
      }).catch(errorResponse => {
        if (schedulePublishDate) {
          onPublishError(errorResponse);
        }
        if (scheduleUnpublishDate) {
          onUnpublishError(errorResponse);
        }
      });
    }
  }), [actionHandler, onPublishError, onUnpublishError]);
};